import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { approvalDecisionSchema, rejectionDecisionSchema } from '../schema/approvalCenterSchema';
import type { ApprovalDecisionFormValues, RejectionDecisionFormValues } from '../schema/approvalCenterSchema';

interface ApprovalDecisionDialogProps {
  mode: 'approve' | 'reject';
  isPending: boolean;
  onConfirm: (comment: string) => void;
  onCancel: () => void;
}

/**
 * ApprovalDecisionDialog — 승인/반려 사유 입력 dialog
 * WAI-ARIA: role=dialog, aria-modal=true, focus trap, ESC 닫기
 */
export function ApprovalDecisionDialog({
  mode,
  isPending,
  onConfirm,
  onCancel,
}: ApprovalDecisionDialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLTextAreaElement | null>(null);

  const isReject = mode === 'reject';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ApprovalDecisionFormValues | RejectionDecisionFormValues>({
    resolver: zodResolver(isReject ? rejectionDecisionSchema : approvalDecisionSchema),
    defaultValues: { comment: '' },
  });

  // 포커스 트랩 + ESC 닫기
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    firstInputRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const onSubmit = (data: ApprovalDecisionFormValues | RejectionDecisionFormValues) => {
    onConfirm(data.comment ?? '');
  };

  const dialogId = `approval-decision-${mode}`;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
      }}
      aria-hidden="false"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${dialogId}-title`}
        aria-describedby={`${dialogId}-desc`}
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: 440,
          maxWidth: '90vw',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <h2
          id={`${dialogId}-title`}
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            marginBottom: 'var(--spacing-3)',
            color: 'var(--color-text-primary)',
          }}
        >
          {isReject
            ? t('approval.dialog.reject.title')
            : t('approval.dialog.approve.title')}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label
              htmlFor={`${dialogId}-comment`}
              id={`${dialogId}-desc`}
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-2)',
              }}
            >
              {isReject
                ? t('approval.dialog.field.reason')
                : t('approval.dialog.field.comment')}
              {isReject && (
                <span style={{ color: 'var(--color-danger)', marginLeft: 'var(--spacing-1)' }}>*</span>
              )}
            </label>
            <textarea
              id={`${dialogId}-comment`}
              {...(() => {
                const { ref: rhfRef, ...rest } = register('comment');
                return {
                  ...rest,
                  ref: (el: HTMLTextAreaElement | null) => {
                    rhfRef(el);
                    firstInputRef.current = el;
                  },
                };
              })()}
              rows={4}
              disabled={isPending}
              aria-required={isReject}
              aria-invalid={!!errors.comment}
              aria-describedby={errors.comment ? `${dialogId}-comment-error` : undefined}
              placeholder={
                isReject
                  ? t('approval.dialog.field.reason')
                  : t('approval.dialog.field.comment')
              }
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: `1px solid ${errors.comment ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                resize: 'vertical',
                outline: 'none',
              }}
            />
            {errors.comment && (
              <p
                id={`${dialogId}-comment-error`}
                role="alert"
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-danger)',
                  marginTop: 'var(--spacing-1)',
                }}
              >
                {errors.comment.message ?? t('approval.dialog.field.reasonRequired')}
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {t('approval.dialog.button.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: isReject
                  ? isPending ? 'var(--color-surface-muted)' : 'var(--color-danger)'
                  : isPending ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                color: isPending ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {isPending ? '...' : t('approval.dialog.button.confirm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
